"use client";

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { datadogRum } from "@datadog/browser-rum";
import { Bug, ImagePlus, Lightbulb, Loader2, OctagonAlert, TriangleAlert, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/ui/form";
import { Input } from "@/ui/input";
import { Button } from "@/ui/button";
import { Alert, AlertDescription } from "@/ui/alert";
import { Link } from "@/ui/link";
import { Text } from "@/ui/text";
import { SegmentedControl } from "@/ui/segmented-control";
import { cn } from "@/ui/cn";
import { formatSpeed } from "@/lib/utils/format";
import { currentEnvironment } from "@/lib/environment";
import { extractRouteIds } from "@/lib/feedback/route-ids";
import type { SubmitFeedbackResult } from "@/lib/feedback/submit-feedback";
import {
  feedbackFormSchema,
  FEEDBACK_ATTACHMENT_ACCEPT,
  FEEDBACK_TYPES,
  FEEDBACK_TYPE_LABELS,
  FEEDBACK_URGENCIES,
  FEEDBACK_URGENCY_LABELS,
  MAX_FEEDBACK_ATTACHMENTS,
  type FeedbackDiagnostics,
  type FeedbackFormValues,
} from "@/lib/schemas/feedback.schemas";
import { version as packageVersion } from "../../../package.json";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DEFAULT_VALUES: FeedbackFormValues = {
  type: "software_issue",
  summary: "",
  details: "",
  urgency: "bug",
  attachments: [],
};

const NO_ATTACHMENTS: File[] = [];

const FALLBACK_ERROR = "Couldn't submit feedback. Please try again.";

type SubmitProgress = { phase: "uploading" | "filing"; percent: number; bytesPerSecond: number };

// Upload progress needs XMLHttpRequest — fetch cannot report it. Same approach as
// `use-file-upload.ts`. Content-Type is left unset so the boundary is generated for us.
function postFeedbackWithProgress(
  formData: FormData,
  onProgress: (progress: SubmitProgress) => void
): Promise<SubmitFeedbackResult> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/feedback");

    const startedAt = performance.now();
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const elapsed = (performance.now() - startedAt) / 1000;
      onProgress({
        phase: "uploading",
        percent: Math.round((event.loaded / event.total) * 100),
        bytesPerSecond: elapsed > 0 ? event.loaded / elapsed : 0,
      });
    };

    // Progress events are not guaranteed: a small body on a fast connection can produce
    // none at all, which would strand the bar at 0% for the whole server round trip — and
    // that round trip is seconds, since the server files the ticket and uploads to Jira
    // before answering. This fires once the body is sent, however many events preceded it.
    xhr.upload.onloadend = () => onProgress({ phase: "filing", percent: 100, bytesPerSecond: 0 });

    // The route answers with a SubmitFeedbackResult whether it accepted or rejected the
    // submission, so every path here resolves rather than throws.
    xhr.onload = () => {
      try {
        resolve(JSON.parse(xhr.responseText) as SubmitFeedbackResult);
      } catch {
        resolve({ success: false, error: FALLBACK_ERROR });
      }
    };
    xhr.onerror = () => resolve({ success: false, error: FALLBACK_ERROR });

    xhr.send(formData);
  });
}

function collectDiagnostics(): FeedbackDiagnostics {
  const rumContext = datadogRum.getInternalContext?.() as { session_id?: string } | undefined;
  return {
    pageUrl: window.location.href,
    routeParams: extractRouteIds(window.location.pathname),
    environment: currentEnvironment().label,
    release: packageVersion,
    appVersion: process.env.NEXT_PUBLIC_DATADOG_VERSION ?? "local",
    userAgent: navigator.userAgent,
    datadogSessionId: rumContext?.session_id,
    datadogReplayUrl: datadogRum.getSessionReplayLink?.() ?? undefined,
  };
}

const TYPE_ICONS: Record<(typeof FEEDBACK_TYPES)[number], typeof Bug> = {
  software_issue: Bug,
  feature_request: Lightbulb,
};

const TYPE_OPTIONS = FEEDBACK_TYPES.map((value) => {
  const Icon = TYPE_ICONS[value];
  return {
    value,
    label: (
      <>
        <Icon className="size-4" />
        {FEEDBACK_TYPE_LABELS[value]}
      </>
    ),
  };
});

const URGENCY_ICONS: Record<(typeof FEEDBACK_URGENCIES)[number], typeof Bug> = {
  bug: TriangleAlert,
  outage: OctagonAlert,
};

const URGENCY_OPTIONS = FEEDBACK_URGENCIES.map((value) => {
  const Icon = URGENCY_ICONS[value];
  return {
    value,
    label: (
      <>
        <Icon className="size-4" />
        {FEEDBACK_URGENCY_LABELS[value]}
      </>
    ),
  };
});

function ScreenshotField({
  files,
  onFilesChange,
  disabled,
}: {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled: boolean;
}) {
  const { formItemId } = useFormField();
  const inputRef = useRef<HTMLInputElement>(null);

  // An object URL is an external resource that has to be created and revoked in the same
  // place, or Strict Mode's double-invoked cleanup revokes URLs nothing will re-mint and
  // every thumbnail comes back blank. That pairing is what the effect buys, and the one
  // setState it costs runs only when the file list itself changes.
  const [previews, setPreviews] = useState<{ file: File; url: string }[]>([]);
  useEffect(() => {
    const minted = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setPreviews(minted);
    return () => minted.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [files]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {previews.map(({ file, url }, index) => (
        <div key={`${file.name}-${index}`} className="relative">
          {/* eslint-disable-next-line @next/next/no-img-element -- a blob: URL, not an asset */}
          <img
            src={url}
            alt={file.name}
            className="border-input size-16 rounded-sm border object-cover"
          />
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            disabled={disabled}
            aria-label={`Remove ${file.name}`}
            onClick={() => onFilesChange(files.filter((_, i) => i !== index))}
            className="bg-background absolute -top-2 -right-2 size-5 rounded-full p-0 shadow-xs"
          >
            <X className="size-3" />
          </Button>
        </div>
      ))}

      {files.length < MAX_FEEDBACK_ATTACHMENTS && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus className="size-4" />
          Add images
        </Button>
      )}

      <input
        id={formItemId}
        ref={inputRef}
        type="file"
        multiple
        accept={FEEDBACK_ATTACHMENT_ACCEPT}
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          // Clearing lets the same file be re-picked after it is removed; without it the
          // input holds the old selection and fires no change event.
          event.target.value = "";
          if (picked.length > 0) onFilesChange([...files, ...picked]);
        }}
      />
    </div>
  );
}

export function FeedbackDialog({ open, onOpenChange }: Props) {
  const [formKey, setFormKey] = useState(0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <FeedbackForm
          key={formKey}
          onClose={() => onOpenChange(false)}
          onReset={() => setFormKey((k) => k + 1)}
        />
      </DialogContent>
    </Dialog>
  );
}

function FeedbackForm({ onClose, onReset }: { onClose: () => void; onReset: () => void }) {
  const [state, setState] = useState<SubmitFeedbackResult | null>(null);
  // Bytes finish moving well before the ticket exists — the server still has to file it
  // and push the images to Jira — so "filing" is a distinct phase, not 100% held.
  const [progress, setProgress] = useState<SubmitProgress | null>(null);
  const isPending = progress !== null;

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (state?.success) {
      datadogRum.addAction("user_feedback", {
        feedback_type: form.getValues("type"),
        summary: form.getValues("summary"),
        jira_issue_key: state.issueKey,
      });
    }
  }, [state, form]);

  function gateSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    void form.handleSubmit((values) => {
      const formData = new FormData(formEl);
      formData.set("diagnostics", JSON.stringify(collectDiagnostics()));
      // A file input's value can't be set from script, so pasted images only exist in
      // form state — every attachment travels this way rather than through the DOM.
      formData.delete("screenshots");
      for (const file of values.attachments ?? NO_ATTACHMENTS) {
        formData.append("screenshots", file);
      }
      void postFeedback(formData);
    })();
  }

  async function postFeedback(formData: FormData) {
    setProgress({ phase: "uploading", percent: 0, bytesPerSecond: 0 });
    const result = await postFeedbackWithProgress(formData, setProgress);
    setProgress(null);
    setState(result);
  }

  function collectPastedImages(e: React.ClipboardEvent<HTMLFormElement>) {
    const pasted = Array.from(e.clipboardData?.files ?? []).filter((file) =>
      file.type.startsWith("image/")
    );
    if (pasted.length === 0) return;
    e.preventDefault();
    const existing = form.getValues("attachments") ?? NO_ATTACHMENTS;
    form.setValue("attachments", [...existing, ...pasted], { shouldValidate: true });
  }

  const submitError = state && !state.success ? state.error : null;
  const succeeded = state?.success === true;
  // react-hook-form's documented watch() API; safe to call despite React Compiler skip
  // eslint-disable-next-line react-hooks/incompatible-library
  const isSoftwareIssue = form.watch("type") === "software_issue";
  const attachmentCount = (form.watch("attachments") ?? NO_ATTACHMENTS).length;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Give feedback</DialogTitle>
        {!succeeded && (
          <DialogDescription>
            Report a software issue or request a feature — we attach session details so we can
            follow up.
          </DialogDescription>
        )}
      </DialogHeader>

      {succeeded && state.success ? (
        <div className="flex flex-col gap-4">
          <Text size="sm" color="muted">
            Thanks — filed as{" "}
            <Link href={state.issueUrl} variant="default">
              {state.issueKey}
            </Link>
            .
          </Text>
          {state.attachmentsFailed ? (
            <Alert variant="warning">
              <AlertDescription>
                {state.attachmentsFailed === 1
                  ? "1 image couldn't be attached"
                  : `${state.attachmentsFailed} images couldn't be attached`}{" "}
                — you can add {state.attachmentsFailed === 1 ? "it" : "them"} to{" "}
                <Link href={state.issueUrl} variant="default">
                  {state.issueKey}
                </Link>{" "}
                directly.
              </AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={onReset}>
              Got another?
            </Button>
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <form onSubmit={gateSubmit} onPaste={collectPastedImages} className="flex flex-col gap-4">
          <Form {...form}>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem orientation="stacked">
                  <FormLabel>Type</FormLabel>
                  <SegmentedControl
                    name="type"
                    options={TYPE_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {isSoftwareIssue && (
              <FormField
                control={form.control}
                name="urgency"
                render={({ field }) => (
                  <FormItem orientation="stacked">
                    <FormLabel>Urgency</FormLabel>
                    <SegmentedControl
                      name="urgency"
                      options={URGENCY_OPTIONS}
                      value={field.value ?? "bug"}
                      onValueChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="summary"
              render={({ field }) => (
                <FormItem orientation="stacked">
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="A short, descriptive title" {...field} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="details"
              render={({ field }) => (
                <FormItem orientation="stacked">
                  <FormLabel>Details</FormLabel>
                  <FormControl>
                    <textarea
                      placeholder="Steps to reproduce, what you expected — anything that helps"
                      rows={4}
                      {...field}
                      className={cn(
                        "border-input placeholder:text-muted-foreground dark:bg-input/15 w-full resize-none rounded-sm border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none",
                        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="attachments"
              render={({ field }) => (
                <FormItem orientation="stacked">
                  <FormLabel>Screenshots</FormLabel>
                  <FormDescription>
                    Paste a screenshot straight into this form, or add up to{" "}
                    {MAX_FEEDBACK_ATTACHMENTS} images.
                  </FormDescription>
                  <ScreenshotField
                    files={field.value ?? NO_ATTACHMENTS}
                    onFilesChange={field.onChange}
                    disabled={isPending}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {submitError && (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            {progress?.phase === "uploading" && attachmentCount > 0 && (
              <div className="flex flex-col gap-1.5">
                <div
                  role="progressbar"
                  aria-label="Upload progress"
                  aria-valuenow={progress.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
                >
                  <div
                    className="bg-primary h-full transition-all duration-300"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <Text size="sm" color="muted">
                  Uploading {attachmentCount === 1 ? "image" : `${attachmentCount} images`} —{" "}
                  {progress.percent}%
                  {progress.bytesPerSecond > 0 && ` · ${formatSpeed(progress.bytesPerSecond)}`}
                </Text>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    {progress.phase === "uploading" && attachmentCount > 0
                      ? `Uploading… ${progress.percent}%`
                      : "Filing…"}
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </DialogFooter>
          </Form>
        </form>
      )}
    </>
  );
}
